/**
 * Zutatenliste mit umgerechneten Mengen und Zutatengruppen. Als Liste auf
 * der Detailseite oder zum Abhaken im Kochmodus.
 */
import { ingredientText, presentIngredient } from '@cookbook/domain';
import type { UnitMode } from '@cookbook/domain';
import { Fragment } from 'react';
import type { Ingredient } from '../api/types.ts';
import { texts } from '../texts/de.ts';
import { CheckItem } from './CheckItem.tsx';

/** Eigenschaften der Zutatenliste. */
export interface IngredientListProps {
  readonly ingredients: readonly Ingredient[];
  readonly factor: number;
  readonly unitMode: UnitMode;
  /** Zum Abhaken: Kennungen der abgehakten Zutaten und Umschalter. */
  readonly checked?: ReadonlySet<string>;
  readonly onToggle?: (key: string) => void;
}

/** Schlüssel einer Zutat in der Prüfliste. */
export function ingredientKey(ingredient: Ingredient): string {
  return `z-${ingredient.id}`;
}

/** Zutatenliste. */
export function IngredientList({ ingredients, factor, unitMode, checked, onToggle }: IngredientListProps) {
  const checklist = checked !== undefined && onToggle !== undefined;
  let lastGroup: string | null = null;

  return (
    <div className={checklist ? 'space-y-2' : 'divide-y divide-border-subtle text-sm'}>
      {ingredients.map((ingredient) => {
        const shown = presentIngredient(ingredient, factor, unitMode);
        const original =
          shown.converted && ingredient.amount !== ''
            ? `${texts.scale.convertedFrom} ${ingredient.amount} ${ingredient.unit}`.trim()
            : null;
        const group = ingredient.group ?? '';
        const heading = group !== '' && group !== lastGroup ? group : null;
        lastGroup = group;
        const note = [ingredient.note, original].filter((part) => part !== null && part !== '').join(' · ');

        return (
          <Fragment key={ingredient.id}>
            {heading === null ? null : (
              <h3 className="pt-3 pb-1 text-xs font-semibold tracking-wide text-accent uppercase">
                {heading}
              </h3>
            )}
            {checklist ? (
              <CheckItem
                label={ingredientText(ingredient, shown)}
                sub={note === '' ? undefined : note}
                checked={checked.has(ingredientKey(ingredient))}
                onToggle={() => onToggle(ingredientKey(ingredient))}
              />
            ) : (
              <div className="flex items-baseline gap-3 py-2">
                <span className="w-24 shrink-0 text-right font-medium tabular-nums">
                  {shown.amount} {shown.unit}
                </span>
                <span className="flex-1">
                  {ingredient.name}
                  {note === '' ? null : <span className="text-content-muted"> ({note})</span>}
                </span>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
